import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, Inbox, BadgePercent, Images, MapPinned, BedDouble, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EnquiriesTab } from "@/components/admin/EnquiriesTab";
import { PromotionsTab } from "@/components/admin/PromotionsTab";
import { PhotosTab } from "@/components/admin/PhotosTab";
import { ContentTab } from "@/components/admin/ContentTab";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { SeasonalRatesPanel } from "@/components/admin/SeasonalRatesPanel";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { Star } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [contentKey, setContentKey] = useState(0);

  const handleLogout = async () => {
    await logout();
    navigate("/master-login");
  };

  return (
    <div className="min-h-screen bg-[#EFEBE1]" data-testid="admin-dashboard">
      <header className="bg-[#0A192F] border-b border-[#C5A059]/25 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="font-serif-display text-xl font-bold text-[#F8F6F0]">Lake Eufaula Inn</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Master Control · {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPwOpen(true)}
              className="flex items-center gap-2 text-sm text-[#F8F6F0]/70 hover:text-[#C5A059] border border-[#F8F6F0]/20 hover:border-[#C5A059] rounded-full px-5 py-2 transition-all"
              data-testid="admin-security-button"
            >
              <ShieldCheck size={14} /> Security
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-[#F8F6F0]/70 hover:text-[#C5A059] border border-[#F8F6F0]/20 hover:border-[#C5A059] rounded-full px-5 py-2 transition-all"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="enquiries" data-testid="admin-tabs">
          <TabsList className="bg-[#0A192F] p-1.5 rounded-full flex flex-wrap h-auto gap-1" data-testid="admin-tabs-list">
            <TabsTrigger value="enquiries" className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0A192F] text-[#F8F6F0]/80 gap-2" data-testid="tab-trigger-enquiries">
              <Inbox size={14} /> Enquiries
            </TabsTrigger>
            <TabsTrigger value="promotions" className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0A192F] text-[#F8F6F0]/80 gap-2" data-testid="tab-trigger-promotions">
              <BadgePercent size={14} /> Promotions
            </TabsTrigger>
            <TabsTrigger value="photos" className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0A192F] text-[#F8F6F0]/80 gap-2" data-testid="tab-trigger-photos">
              <Images size={14} /> Photos (JPEG)
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0A192F] text-[#F8F6F0]/80 gap-2" data-testid="tab-trigger-reviews">
              <Star size={14} /> Reviews
            </TabsTrigger>
            <TabsTrigger value="guide" className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0A192F] text-[#F8F6F0]/80 gap-2" data-testid="tab-trigger-guide">
              <MapPinned size={14} /> Town Guide & Dining
            </TabsTrigger>
            <TabsTrigger value="hotel" className="rounded-full data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#0A192F] text-[#F8F6F0]/80 gap-2" data-testid="tab-trigger-hotel">
              <BedDouble size={14} /> Hotel & Rooms
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-8"><EnquiriesTab /></TabsContent>
          <TabsContent value="promotions" className="mt-8"><PromotionsTab /></TabsContent>
          <TabsContent value="photos" className="mt-8"><PhotosTab /></TabsContent>
          <TabsContent value="reviews" className="mt-8"><ReviewsTab /></TabsContent>
          <TabsContent value="guide" className="mt-8"><ContentTab section="guide" /></TabsContent>
          <TabsContent value="hotel" className="mt-8">
            <SeasonalRatesPanel onChanged={() => setContentKey((k) => k + 1)} />
            <ContentTab key={contentKey} section="hotel" />
          </TabsContent>
        </Tabs>
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
